import React, { useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

import { DragHandleSmallIcon } from 'src/monet/icons';

import { Document } from 'src/common/interfaces/document.interface';
import { DocumentBlockContentItem } from 'src/common/interfaces/document_block.interface';

export const TextEditorBlocksSummary: React.FC<{
  document: Document;
  onDragEnd: (result: DropResult) => void;
}> = ({ document, onDragEnd }) => {
  const { blocks } = document;

  const getBlockSummary = useCallback((block: DocumentBlockContentItem) => {
    if (!block) return null;

    const blockType = block.type;

    if (blockType === 'paragraph' || blockType === 'heading') {
      const firstTextBlock = block.content?.find((block: any) => block.type === 'text');
      return firstTextBlock ? firstTextBlock.text.substring(0, 50) : null;
    } else if (blockType === 'mutualActionPlan') {
      const description = block.attrs.description;
      const firstNoneEmptyItem = block.attrs.items?.find(
        (mutualActionPlanItem: any) => mutualActionPlanItem.values.value
      );
      return description
        ? description.substring(0, 50)
        : firstNoneEmptyItem
        ? firstNoneEmptyItem.values.value.substring(0, 50)
        : null;
    }

    return null;
  }, []);

  const getFriendlyBlockTypeName = useCallback((block: DocumentBlockContentItem) => {
    if (!block) return null;

    const blockType = block.type;

    switch (blockType) {
      case 'paragraph':
        return 'Paragraph';
      case 'heading':
        return 'Heading';
      case 'mutualActionPlan':
        return 'Mutual Action Plan';
      case 'image':
        return 'Image';
      case 'iframe':
        return 'Embed';
      case 'table':
        return 'Table';
      default:
        return blockType;
    }
  }, []);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId='droppable-document-editor-summary' type='content'>
        {(provided: any, snapshot: any) => (
          <div ref={provided.innerRef}>
            <div className='flex flex-col space-y-2 max-w-[180px]'>
              {blocks.map((block: any, idx: number) => {
                if (!block) return null;
                const firstContentInBlock = block.content ? block.content[0] : null;

                const blockTypeName = getFriendlyBlockTypeName(firstContentInBlock);
                const blockSummary = getBlockSummary(firstContentInBlock);

                return (
                  <Draggable key={`block-${idx}`} draggableId={`block-${idx}`} index={idx}>
                    {(provided: any, snapshot: any) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} style={provided.draggableProps.style}>
                        <div key={`${block.type}-${idx}`}>
                          <div className='flex items-center space-x-1 relative'>
                            <div {...provided.dragHandleProps}>
                              <DragHandleSmallIcon />
                            </div>
                            <div className='text-bedrock-p-strong text-bedrock-black truncate'>
                              {blockSummary || blockTypeName}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
